import styles from '../MyHomes/MyHomes.module.css';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import {
  addBuilding,
  getBuildingDetails,
  getBuildingDetailsById,
  getTenantDetails,
  updateBuilding,
  updatePersonalInfo,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { useNavigate } from 'react-router-dom';

import { Button, Tooltip, Whisper } from 'rsuite';
import infoIcon from '../../../assets/pages/addProperty/rentalInfo/infoIcon.svg';
import { ArrowRight, ChevronLeft, Pencil, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import { addProperty2 } from '../../utils/addPropertyScehmas/addProperty2Schema';
import { rentAddressOccupationSchema } from '../../utils/addPropertyScehmas/rentAddressOccupationSchema';
import { addressOccupationSchema } from '../../utils/addPropertyScehmas/addressOccupationSchema';
import { motion } from 'framer-motion';
import { Dollar } from '../../../pages/addProperty/AddProperty/AddProperty';
import { showToast } from '../../utils/showToast';
import { BuildingType, RentAddressOccupationFormValues } from '../../DatePicker/DatePicker';
import { FetchAddress } from '../../utils/utils';
import { PersonalInfoRequest } from '../../utils/types';
import { trackOnboardingCompletedOnce } from '../../utils/analytics';
import { hasUsableBuildingAddress } from '../../utils/payFlowGuard';
import { DueDateCalendar } from '../../components/DueDateCalendar/DueDateCalendar';
import { openIdentityVerificationModal } from '../../utils/login';

const initialValues: RentAddressOccupationFormValues = {
  rent: null as number | null,
  dueDay: 1,
  address: '',
  unitNo: '',
  street: '',
  house: '',
  city: '',
  province: '',
  provinceCode: '',
  postal: '',
  country: 'Canada',
  occupation: '',
};

const AddHome = () => {
  const { auth } = useAuth();
  const { setModal, modalState } = useModal();
  const navigate = useNavigate();
  const payRentSetupFlow = modalState?.payRentSetupFlow === true;
  const isNewBuilding = modalState?.isNewBuilding === true;
  const postIdvDetailsFlow = modalState?.postIdvDetailsFlow === true;
  const isFullAddHomeFlow = payRentSetupFlow || isNewBuilding || postIdvDetailsFlow;
  const showAddressFields = isNewBuilding || postIdvDetailsFlow;
  const showRentAndDueDate = !postIdvDetailsFlow;
  const showOccupation = postIdvDetailsFlow || showRentAndDueDate;
  const existingBuildingId = modalState?.tenantBuildingId;
  const propertyPrefill = modalState?.propertyPrefill;
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [dueDay, setDueDay] = useState<number>(1);
  const [formattedRent, setFormattedRent] = useState<string>('');
  const [accountType, setAccountType] = useState<string>('');
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [isAutoCompleteOpen, setIsAutocompleteOpen] = useState(false);

  // popup states
  const [showAccountPopup, setShowAccountPopup] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const validationSchema = postIdvDetailsFlow
    ? addressOccupationSchema
    : isNewBuilding
      ? rentAddressOccupationSchema
      : addProperty2;

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      // if BOTH and no selection yet → show popup (only when creating a new building)
      if (!existingBuildingId && accountType === 'both' && !selectedType) {
        setShowAccountPopup(true);
        setSubmitting(false);
        return;
      }

      setLoading(true);

      try {
        if (formik.isValid) {
          let buildingId = existingBuildingId;

          if (!buildingId && !postIdvDetailsFlow) {
            const requestBody = {
              buildingDetails: {
                unitNumber: '',
                buildingName: '',
                rentAmount: Number(values.rent),
                dueDate: values.dueDay,
                type: (selectedType as BuildingType) || (accountType as BuildingType),
              },
            };
            const addResponse = await addBuilding(auth?.data.token, requestBody);
            buildingId = (
              addResponse as { data?: { tenantBuildingId?: string | number } }
            ).data?.tenantBuildingId?.toString();

            if (!buildingId) {
              const buildingRes = await getBuildingDetails(auth?.data.token);
              const buildings = buildingRes?.data || [];
              buildingId = buildings[buildings.length - 1]?.building?.tenantBuildingId;
            }
          }

          if (buildingId) {
            if (postIdvDetailsFlow) {
              await updateBuilding(auth?.data.token, buildingId, {
                addressDetails: {
                  streetName: values.street,
                  city: values.city,
                  province: values.province,
                  provinceCode: values.provinceCode,
                  country: 'Canada',
                  countryCode: 'CA',
                  postalCode: values.postal,
                  fullAddress: values.address,
                  buildingNumber: values.house,
                  unitNumber: values.unitNo,
                },
              });

              if (values.occupation) {
                await updatePersonalInfo(auth?.data.token, {
                  tenantDetails: {
                    occupation: values.occupation,
                  },
                } as PersonalInfoRequest);
              }

              showToast({
                message: 'Details saved successfully!',
                type: 'success',
              });

              await openIdentityVerificationModal(
                setModal,
                auth?.data.token || '',
                buildingId,
                {
                  pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
                  skipPrerequisiteDetailsCheck: true,
                }
              );
              setLoading(false);
              return;
            }

            if (values.occupation) {
              await updatePersonalInfo(auth?.data.token, {
                tenantDetails: {
                  occupation: values.occupation,
                },
              } as PersonalInfoRequest);
            }

            const hasPrefillAddress = Boolean(values.street && values.city && values.postal);

            if (isNewBuilding) {
              await updateBuilding(auth?.data.token, buildingId, {
                buildingDetails: {
                  rentAmount: Number(values.rent),
                  dueDate: values.dueDay,
                },
                addressDetails: {
                  streetName: values.street,
                  city: values.city,
                  province: values.province,
                  provinceCode: values.provinceCode,
                  country: 'Canada',
                  countryCode: 'CA',
                  postalCode: values.postal,
                  fullAddress: values.address,
                  buildingNumber: values.house,
                  unitNumber: values.unitNo,
                },
              });
            } else if (payRentSetupFlow) {
              const buildingDetails = {
                rentAmount: Number(values.rent),
                dueDate: values.dueDay,
              };
              if (hasPrefillAddress) {
                await updateBuilding(auth?.data.token, buildingId, {
                  buildingDetails,
                  addressDetails: {
                    streetName: values.street,
                    city: values.city,
                    province: values.province,
                    provinceCode: values.provinceCode,
                    country: 'Canada',
                    countryCode: 'CA',
                    postalCode: values.postal,
                    fullAddress: values.address,
                    buildingNumber: values.house,
                    unitNumber: values.unitNo,
                  },
                });
              } else {
                await updateBuilding(auth?.data.token, buildingId, {
                  buildingDetails,
                });
              }
            }
          }

          showToast({
            message: 'Home added successfully!',
            type: 'success',
          });

          if (payRentSetupFlow) {
            trackOnboardingCompletedOnce(auth?.data.tenantId);
          }

          if (payRentSetupFlow && buildingId) {
            setModal('add-payment-method', {
              tenantBuildingId: buildingId,
              payRentSetupFlow: true,
              refreshProperties: true,
              refreshProfile: true,
            });
            setLoading(false);
            return;
          }

          setModal(null, { refreshProperties: true });
          setLoading(false);
          navigate('/pay-rent', {
            state: {
              buildingId,
              initialStep: 1,
              syncPayFlowFromState: true,
            },
          });
        }
      } catch (error) {
        setLoading(false);
        console.error('Error Adding buildings:', error);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    (async () => {
      const tenantDetails = await getTenantDetails(auth?.data?.token);
      setAccountType(tenantDetails?.data?.tenant?.accountType);
      const occupation = tenantDetails?.data?.tenant?.occupation;
      if (occupation) {
        formik.setFieldValue('occupation', occupation);
      }
      if (postIdvDetailsFlow) {
        if (existingBuildingId) {
          try {
            const response = await getBuildingDetailsById(auth?.data?.token, existingBuildingId);
            const address = response?.data?.[0]?.address;
            if (hasUsableBuildingAddress(address)) {
              setIsManualAddress(true);
              void formik.setFieldValue('address', address.fullAddress || '');
              void formik.setFieldValue('unitNo', address.unitNumber || '');
              void formik.setFieldValue('street', address.streetName || '');
              void formik.setFieldValue('house', address.buildingNumber || '');
              void formik.setFieldValue('city', address.city || '');
              void formik.setFieldValue('province', address.province || '');
              void formik.setFieldValue('provinceCode', address.provinceCode || '');
              void formik.setFieldValue('postal', address.postalCode || '');
              void formik.setFieldValue('country', address.country || 'Canada');
            }
          } catch (error) {
            console.error('Error fetching building address:', error);
          }
        }
      }
    })();
  }, [auth, postIdvDetailsFlow, existingBuildingId]);

  useEffect(() => {
    if (!propertyPrefill) {
      return;
    }

    const rentValue = propertyPrefill.rent ?? null;
    if (rentValue != null) {
      setFormattedRent(Number(rentValue).toLocaleString());
    }

    const hasAddressDetails = Boolean(
      propertyPrefill.address ||
      propertyPrefill.street ||
      propertyPrefill.city ||
      propertyPrefill.postal
    );
    if (hasAddressDetails && showAddressFields) {
      setIsManualAddress(true);
    }

    void formik.setFieldValue('rent', rentValue);
    void formik.setFieldValue('address', propertyPrefill.address || '');
    void formik.setFieldValue('unitNo', propertyPrefill.unitNo || '');
    void formik.setFieldValue('street', propertyPrefill.street || '');
    void formik.setFieldValue('house', propertyPrefill.house || '');
    void formik.setFieldValue('city', propertyPrefill.city || '');
    void formik.setFieldValue('province', propertyPrefill.province || '');
    void formik.setFieldValue('provinceCode', propertyPrefill.provinceCode || '');
    void formik.setFieldValue('postal', propertyPrefill.postal || '');
    void formik.setFieldValue('country', propertyPrefill.country || 'Canada');
  }, [propertyPrefill]);

  useEffect(() => {
    if (!showAddressFields) {
      return;
    }

    if (
      (formik.errors.address ||
        formik.errors.city ||
        formik.errors.country ||
        formik.errors.province ||
        formik.errors.provinceCode ||
        formik.errors.postal) &&
      formik.touched.address
    ) {
      setIsManualAddress(true);
    } else if (!isNewBuilding && !postIdvDetailsFlow) {
      setIsManualAddress(false);
    }
  }, [
    formik.errors.address,
    formik.errors.city,
    formik.errors.country,
    formik.errors.province,
    formik.errors.provinceCode,
    formik.errors.postal,
    formik.touched.address,
    showAddressFields,
    isNewBuilding,
    postIdvDetailsFlow,
  ]);

  const isSubmitDisabled =
    loading || !formik.isValid || (!isFullAddHomeFlow && !formik.dirty);

  const handleRentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^0-9.]/g, '');
    if (value && value !== '0') {
      const parts = value.split('.');
      if (parts.length === 2) {
        setFormattedRent(`${parseInt(parts[0], 10).toLocaleString()}.${parts[1]}`);
      } else {
        setFormattedRent(parseInt(value, 10).toLocaleString());
      }
      formik.setFieldValue('rent', value);
    } else {
      setFormattedRent(value);
      formik.setFieldValue('rent', value);
    }
  };

  const renderRentAndDueDate = () => (
    <>
      <div className='inputWrapper'>
        <label className={'label'}>
          Monthly rent amount <span className={styles.mandatory}>*</span>
        </label>

        <div
          className={clsx(
            formik.touched.rent && formik.errors.rent ? styles.isInvalid : '',
            formik.touched.rent && !formik.errors.rent ? styles.valid : '',
            `inputField`
          )}
        >
          {formik.values.rent && <Dollar />}
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('rent')}
            value={
              formattedRent ||
              (formik.values.rent !== null ? formik.values.rent.toLocaleString() : '')
            }
            placeholder='Enter your monthly rent amount'
            onChange={handleRentChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
        </div>
        {formik.touched.rent && formik.errors.rent && (
          <div className={styles.error}>
            <span>{formik.errors.rent}</span>
          </div>
        )}
      </div>
    </>
  );


  const renderManualAddressFields = () => (
    <>
      <div className={styles.fieldFlex}>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>Building or street number</label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('house')}
            placeholder='Enter your building/street number'
            className={clsx(
              formik.touched.house && formik.errors.house && formik.values.house !== ''
                ? styles.isInvalid
                : '',
              formik.touched.house && !formik.errors.house && formik.values.house !== ''
                ? styles.valid
                : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.house && formik.errors.house && (
            <div className={styles.error}>
              <span>{formik.errors.house}</span>
            </div>
          )}
        </div>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>Unit number</label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('unitNo')}
            placeholder='Enter your unit number'
            className={clsx(
              formik.touched.unitNo && formik.errors.unitNo && formik.values.unitNo !== ''
                ? styles.isInvalid
                : '',
              formik.touched.unitNo && !formik.errors.unitNo && formik.values.unitNo !== ''
                ? styles.valid
                : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.unitNo && formik.errors.unitNo && (
            <div className={styles.error}>
              <span>{formik.errors.unitNo}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.fieldFlex}>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>
              Street name<span className={styles.imp}>*</span>
            </label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('street')}
            placeholder='Enter your street name'
            className={clsx(
              formik.touched.street && formik.errors.street ? styles.isInvalid : '',
              formik.touched.street && !formik.errors.street ? styles.valid : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.street && formik.errors.street && (
            <div className={styles.error}>
              <span>{formik.errors.street}</span>
            </div>
          )}
        </div>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>
              City<span className={styles.imp}>*</span>
            </label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('city')}
            placeholder='Enter your city'
            className={clsx(
              formik.touched.city && formik.errors.city ? styles.isInvalid : '',
              formik.touched.city && !formik.errors.city ? styles.valid : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.city && formik.errors.city && (
            <div className={styles.error}>
              <span>{formik.errors.city}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.fieldFlex}>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>
              Province<span className={styles.imp}>*</span>
            </label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('province')}
            placeholder='Enter your province'
            className={clsx(
              formik.touched.province && formik.errors.province ? styles.isInvalid : '',
              formik.touched.province && !formik.errors.province ? styles.valid : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.province && formik.errors.province && (
            <div className={styles.error}>
              <span>{formik.errors.province}</span>
            </div>
          )}
        </div>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>
              Postal code<span className={styles.imp}>*</span>
            </label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('postal')}
            placeholder='Enter postal code'
            className={clsx(
              formik.touched.postal && formik.errors.postal ? styles.isInvalid : '',
              formik.touched.postal && !formik.errors.postal ? styles.valid : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.postal && formik.errors.postal && (
            <div className={styles.error}>
              <span>{formik.errors.postal}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.fieldFlex}>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <div className={styles.labelOuter}>
            <label className='label'>
              Province code<span className={styles.imp}>*</span>
            </label>
          </div>
          <input
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('provinceCode')}
            placeholder='Enter your province Code'
            className={clsx(
              formik.touched.provinceCode && formik.errors.provinceCode ? styles.isInvalid : '',
              formik.touched.provinceCode && !formik.errors.provinceCode ? styles.valid : '',
              'inputField'
            )}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          {formik.touched.provinceCode && formik.errors.provinceCode && (
            <div className={styles.error}>
              <span>{formik.errors.provinceCode}</span>
            </div>
          )}
        </div>
        <div className={`${styles.fieldOuter} inputWrapper`}>
          <label className={'label'}>
            Country<span className={styles.imp}>*</span>
          </label>
          <div className={`${styles.countryName} inputField`}>Canada</div>
        </div>
      </div>
    </>
  );

  return (
    <motion.main
      className={clsx(styles.main, styles.main2, isAutoCompleteOpen && 'overflow-hidden')}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {showAddressFields && (
        <FetchAddress
          addressInputRef={addressInputRef}
          formik={formik}
          isSaveManually={setIsManualAddress}
          setIsAutocompleteOpen={setIsAutocompleteOpen}
        />
      )}

      {/* POPUP for selecting personal/business */}
      {showAccountPopup && (
        <div className='fixed inset-0 flex items-center justify-center bg-black/40 z-[9999]'>
          <div className='bg-white rounded-xl p-6 w-[320px] text-center shadow-xl relative'>
            <h2 className='text-lg font-semibold mb-2'>Select account type</h2>
            <p className='text-sm text-gray-600 mb-4'>Do you want to add this home under:</p>

            <button
              className='submitBtn'
              onClick={() => {
                setSelectedType('personal');
                setShowAccountPopup(false);
                formik.handleSubmit();
              }}
            >
              Personal account
            </button>

            <button
              className='submitBtn mt-5'
              onClick={() => {
                setSelectedType('business');
                setShowAccountPopup(false);
                formik.handleSubmit();
              }}
            >
              Business account
            </button>

            <X
              className='absolute top-3 right-3 cursor-pointer text-gray-500 hover:text-gray-700'
              size={20}
              onClick={() => setShowAccountPopup(false)}
            />
          </div>
        </div>
      )}

      <div className={styles.headingOuter}>
        {showAddressFields && isManualAddress ? (
          <div className='flex items-center gap-[12px]'>
            <button type='button' onClick={() => setIsManualAddress(false)}>
              <ChevronLeft stroke='#0E0E0B' />
            </button>
            <span>Enter address manually</span>
          </div>
        ) : (
          <div className='flex items-center gap-[12px]'>
            {isFullAddHomeFlow && !postIdvDetailsFlow && (
              <button
                type='button'
                onClick={() =>
                  setModal('complete-building', {
                    tenantBuildingId: existingBuildingId,
                    payRentSetupFlow,
                    isNewBuilding,
                    propertyPrefill,
                  })
                }
              >
                <ChevronLeft stroke='#0E0E0B' />
              </button>
            )}
            <span>
              {postIdvDetailsFlow
                ? 'Complete your details'
                : isFullAddHomeFlow
                  ? 'Property details'
                  : 'Add home'}
            </span>
          </div>
        )}
        {!payRentSetupFlow && (
          <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
        )}
      </div>

      <div className={clsx(styles.formOuter, showAddressFields && styles.formOuterScroll)}>
        {isFullAddHomeFlow && !postIdvDetailsFlow && !isManualAddress && (
          <p className={styles.formSubtitle}>
            We use these details to schedule your rent payments accurately and verify your
            account for compliance.
          </p>
        )}
        <form
          className={clsx(styles.form, isFullAddHomeFlow && styles.formCompact)}
          noValidate
          onSubmit={formik.handleSubmit}
        >
          {showAddressFields && (
            <div className='inputWrapper'>
              <div className={styles.labelOuter}>
                <label className='label'>
                  Rental property address<span className={styles.mandatory}>*</span>
                </label>
                {!isManualAddress && (
                  <button
                    type='button'
                    className={styles.editOuter}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsManualAddress(true);
                    }}
                  >
                    <Pencil width={20} height={20} />
                    <span>Enter manually</span>
                  </button>
                )}
              </div>
              <input
                type='text'
                ref={addressInputRef}
                autoComplete='off'
                {...formik.getFieldProps('address')}
                placeholder='Enter your full address'
                className={clsx(
                  formik.touched.address && formik.errors.address ? styles.isInvalid : '',
                  formik.touched.address && !formik.errors.address ? styles.valid : '',
                  'inputField'
                )}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
              {formik.touched.address && formik.errors.address && (
                <div className={styles.error}>
                  <span>{formik.errors.address}</span>
                </div>
              )}
            </div>
          )}

          {showAddressFields && isManualAddress && renderManualAddressFields()}

          {showRentAndDueDate && renderRentAndDueDate()}

          {(showRentAndDueDate || showOccupation) && (
            <div className={styles.dueDayOccupationRow}>
              {showRentAndDueDate && (
                <div className='inputWrapper' style={{ flex: 1 }}>
                  <div className={styles.tooltipWrap}>
                    <label className='label'>
                      Rent due day each month<span className={styles.mandatory}>*</span>
                    </label>
                    <Whisper
                      trigger='hover'
                      placement='right'
                      controlId='control-id-due-day'
                      speaker={
                        <Tooltip>
                          <p>
                            Select a day between the 1st and 28th, your landlord will receive the
                            rent payment by this day each month. Days 7–28 earn points!
                          </p>
                        </Tooltip>
                      }
                    >
                      <div className='signUpBtn'>
                        <Button appearance='subtle' className={styles.btnImg}>
                          <img src={infoIcon} className={styles.infoIcon} alt='info' />
                        </Button>
                      </div>
                    </Whisper>
                  </div>
                  <DueDateCalendar
                    selectedDay={dueDay}
                    onSelectDay={(day) => {
                      setDueDay(day);
                      formik.setFieldValue('dueDay', day);
                    }}
                  />
                  {formik.touched.dueDay && formik.errors.dueDay && (
                    <div className={styles.error}>
                      <span>{formik.errors.dueDay}</span>
                    </div>
                  )}
                </div>
              )}
              {showOccupation && (
                <div style={{ flex: 1 }} className='inputWrapper'>
                  <div className={styles.labelOuter}>
                    <label className='label'>
                      Your occupation<span className={styles.mandatory}>*</span>
                    </label>
                  </div>
                  <input
                    type='text'
                    autoComplete='off'
                    {...formik.getFieldProps('occupation')}
                    placeholder='Enter your occupation'
                    className={clsx(
                      formik.touched.occupation && formik.errors.occupation
                        ? styles.isInvalid
                        : '',
                      formik.touched.occupation && !formik.errors.occupation ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                  <p className={styles.helperText}>We need to get this for compliance.</p>
                  {formik.touched.occupation && formik.errors.occupation && (
                    <div className={styles.error}>
                      <span>{formik.errors.occupation}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


          <button
            className={clsx(
              styles.nextBtn,
              !isSubmitDisabled && styles.submitBtn,
              !isFullAddHomeFlow && styles.marginTBtn
            )}
            disabled={isSubmitDisabled}
            type='submit'
          >
            <span>{loading ? 'Please wait...' : 'Next'}</span>
            <ArrowRight stroke={isSubmitDisabled ? '#0E0E0B66' : '#fff'} />
          </button>
        </form>
      </div>
    </motion.main>
  );
};

export default AddHome;
